import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { relatorApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft, CheckCircle, XCircle, FloppyDisk, LockOpen, Exam, ClipboardText, Users, Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const statusConfig = {
  sin_conformar: { label: 'Sin Conformar', className: 'bg-amber-500/10 text-amber-600' },
  conformada: { label: 'Conformada', className: 'bg-blue-500/10 text-blue-600' },
  realizada: { label: 'Realizada', className: 'bg-green-500/10 text-green-600' },
};

export default function RelatorTrainingDetail() {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('attendance');
  const [attendance, setAttendance] = useState({});
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [scores, setScores] = useState({});
  const [savingEval, setSavingEval] = useState(false);
  const [closeConfirm, setCloseConfirm] = useState(false);
  const [closing, setClosing] = useState(false);

  const fetchTraining = useCallback(async () => {
    try {
      const res = await relatorApi.getTrainingDetail(trainingId);
      setTraining(res.data);
      // Initialize attendance
      const att = {};
      res.data.enrollments.forEach(e => { att[e.enrollment_id] = e.attended; });
      setAttendance(att);
      // Initialize scores from existing
      setScores(res.data.scores || {});
      // Select first instrument by default
      if (res.data.instruments?.length > 0) {
        setSelectedInstrument(prev => prev || res.data.instruments[0].id);
      }
    } catch { toast.error('Error al cargar capacitacion'); }
    finally { setLoading(false); }
  }, [trainingId]);

  useEffect(() => { fetchTraining(); }, [fetchTraining]);

  const handleSaveAttendance = async () => {
    setSavingAttendance(true);
    try {
      await relatorApi.markAttendance(trainingId, { attendance });
      toast.success('Asistencia guardada');
      fetchTraining();
    } catch { toast.error('Error al guardar asistencia'); }
    finally { setSavingAttendance(false); }
  };

  const handleSaveEvaluation = async () => {
    if (!selectedInstrument) return;
    setSavingEval(true);
    const instrument = training.instruments.find(i => i.id === selectedInstrument);
    if (!instrument) return;
    
    const scoresToSend = [];
    training.enrollments.forEach(e => {
      instrument.criteria.forEach(c => {
        const key = `${e.enrollment_id}_${c.id}`;
        if (scores[key] !== undefined) {
          scoresToSend.push({
            enrollment_id: e.enrollment_id,
            criterion_id: c.id,
            score: scores[key]?.score ?? scores[key],
            notes: scores[key]?.notes || '',
          });
        }
      });
    });

    try {
      await relatorApi.evaluate(trainingId, { scores: scoresToSend });
      toast.success('Evaluacion guardada');
      fetchTraining();
    } catch { toast.error('Error al guardar evaluacion'); }
    finally { setSavingEval(false); }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      await relatorApi.closeTraining(trainingId);
      toast.success('Capacitacion cerrada');
      setCloseConfirm(false);
      fetchTraining();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al cerrar'); }
    finally { setClosing(false); }
  };

  const getScoreValue = (enrollmentId, criterionId) => {
    const key = `${enrollmentId}_${criterionId}`;
    const s = scores[key];
    if (s === undefined || s === null) return '';
    if (typeof s === 'object') return s.score ?? '';
    return s;
  };

  const setScoreValue = (enrollmentId, criterionId, value) => {
    const key = `${enrollmentId}_${criterionId}`;
    const existing = scores[key];
    const notes = typeof existing === 'object' ? existing?.notes || '' : '';
    setScores(prev => ({ ...prev, [key]: { score: value, notes } }));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
    </div>
  );

  if (!training) return <div className="text-center py-16 text-[#2B4B8C]/40">Capacitacion no encontrada</div>;

  const isClosed = training.status === 'realizada';
  const currentInstrument = training.instruments?.find(i => i.id === selectedInstrument);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="relator-training-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/relator')} className="text-[#2B4B8C]/60">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#2B4B8C]">{training.name}</h1>
            <Badge variant="outline" className={statusConfig[training.status]?.className}>{statusConfig[training.status]?.label || training.status}</Badge>
          </div>
          <div className="flex items-center gap-4 text-sm text-[#2B4B8C]/50 mt-1">
            {training.course_name && <span>{training.course_name}</span>}
            {training.client_name && <span>- {training.client_name}</span>}
            <span>{training.scheduled_date ? new Date(training.scheduled_date).toLocaleDateString('es-CL') : ''}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#2B4B8C]/5">
          <TabsTrigger value="attendance" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-attendance">
            <Users size={16} className="mr-1.5" /> Asistencia
          </TabsTrigger>
          <TabsTrigger value="evaluation" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-evaluation">
            <ClipboardText size={16} className="mr-1.5" /> Evaluacion
          </TabsTrigger>
          <TabsTrigger value="tests" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-tests">
            <Exam size={16} className="mr-1.5" /> Resultados Tests
          </TabsTrigger>
          <TabsTrigger value="close" className="data-[state=active]:bg-white data-[state=active]:text-[#2B4B8C]" data-testid="tab-close">
            <LockOpen size={16} className="mr-1.5" /> Cerrar
          </TabsTrigger>
        </TabsList>

        {/* Attendance Tab */}
        <TabsContent value="attendance">
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-0">
              {training.enrollments.length > 0 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#2B4B8C]/5">
                        <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                        <TableHead className="text-[#2B4B8C]/60">Nombre</TableHead>
                        <TableHead className="text-[#2B4B8C]/60">Empresa</TableHead>
                        <TableHead className="text-center text-[#2B4B8C]/60">Presente</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {training.enrollments.map(e => (
                        <TableRow key={e.enrollment_id} className="border-[#2B4B8C]/5" data-testid={`attendance-row-${e.enrollment_id}`}>
                          <TableCell className="text-[#2B4B8C]/70 text-sm">{e.rut || '-'}</TableCell>
                          <TableCell className="font-medium text-[#2B4B8C]">{e.first_name} {e.last_name}</TableCell>
                          <TableCell className="text-[#2B4B8C]/60 text-sm">{e.company_name || '-'}</TableCell>
                          <TableCell className="text-center">
                            <Checkbox
                              checked={attendance[e.enrollment_id] || false}
                              onCheckedChange={(checked) => setAttendance(prev => ({ ...prev, [e.enrollment_id]: !!checked }))}
                              disabled={isClosed}
                              data-testid={`attendance-check-${e.enrollment_id}`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {!isClosed && (
                    <div className="p-4 border-t border-[#2B4B8C]/5 flex justify-end">
                      <Button onClick={handleSaveAttendance} disabled={savingAttendance} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="save-attendance-btn">
                        <FloppyDisk size={16} className="mr-1.5" /> {savingAttendance ? 'Guardando...' : 'Guardar Asistencia'}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16 text-[#2B4B8C]/40">
                  <Users size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay trabajadores inscritos</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evaluation Tab */}
        <TabsContent value="evaluation">
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-6">
              {training.instruments?.length > 0 ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <label className="text-sm font-medium text-[#2B4B8C]">Instrumento:</label>
                    <Select value={selectedInstrument || ''} onValueChange={setSelectedInstrument}>
                      <SelectTrigger className="w-64 bg-[#f8fafc] border-[#2B4B8C]/10" data-testid="instrument-select">
                        <SelectValue placeholder="Seleccionar instrumento" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {training.instruments.map(i => (
                          <SelectItem key={i.id} value={i.id}>{i.name} ({i.instrument_type === 'checklist' ? 'Tabla de Cotejo' : 'Rubrica'})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {currentInstrument && (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-[#2B4B8C]/5">
                            <TableHead className="text-[#2B4B8C]/60 sticky left-0 bg-white min-w-[180px]">Trabajador</TableHead>
                            {currentInstrument.criteria.map(c => (
                              <TableHead key={c.id} className="text-[#2B4B8C]/60 text-center min-w-[120px]">
                                <div className="text-xs">{c.name}</div>
                                {currentInstrument.instrument_type === 'rubric' && (
                                  <div className="text-[10px] text-[#2B4B8C]/40">Max: {c.max_score}</div>
                                )}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {training.enrollments.map(e => (
                            <TableRow key={e.enrollment_id} className="border-[#2B4B8C]/5" data-testid={`eval-row-${e.enrollment_id}`}>
                              <TableCell className="font-medium text-[#2B4B8C] sticky left-0 bg-white">
                                <div>{e.first_name} {e.last_name}</div>
                                <div className="text-[10px] text-[#2B4B8C]/40">{e.rut}</div>
                              </TableCell>
                              {currentInstrument.criteria.map(c => (
                                <TableCell key={c.id} className="text-center">
                                  {currentInstrument.instrument_type === 'checklist' ? (
                                    <Checkbox
                                      checked={getScoreValue(e.enrollment_id, c.id) === 1}
                                      onCheckedChange={(checked) => setScoreValue(e.enrollment_id, c.id, checked ? 1 : 0)}
                                      disabled={isClosed}
                                      data-testid={`score-${e.enrollment_id}-${c.id}`}
                                    />
                                  ) : (
                                    <Input
                                      type="number"
                                      min="0"
                                      max={c.max_score}
                                      step="0.5"
                                      value={getScoreValue(e.enrollment_id, c.id)}
                                      onChange={(ev) => setScoreValue(e.enrollment_id, c.id, parseFloat(ev.target.value) || 0)}
                                      disabled={isClosed}
                                      className="w-16 mx-auto text-center border-[#2B4B8C]/10"
                                      data-testid={`score-${e.enrollment_id}-${c.id}`}
                                    />
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {!isClosed && (
                        <div className="mt-4 flex justify-end">
                          <Button onClick={handleSaveEvaluation} disabled={savingEval} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="save-eval-btn">
                            <FloppyDisk size={16} className="mr-1.5" /> {savingEval ? 'Guardando...' : 'Guardar Evaluacion'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16 text-[#2B4B8C]/40">
                  <ClipboardText size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay instrumentos de evaluacion asignados a este curso</p>
                  <p className="text-xs mt-1">El administrador debe crear instrumentos en la configuracion del curso</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Test Results Tab */}
        <TabsContent value="tests">
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-0">
              {training.test_results?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2B4B8C]/5">
                      <TableHead className="text-[#2B4B8C]/60">Trabajador</TableHead>
                      <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                      <TableHead className="text-center text-[#2B4B8C]/60">Intento</TableHead>
                      <TableHead className="text-center text-[#2B4B8C]/60">Puntaje</TableHead>
                      <TableHead className="text-center text-[#2B4B8C]/60">Resultado</TableHead>
                      <TableHead className="text-[#2B4B8C]/60">Fecha</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {training.test_results.map((r, idx) => (
                      <TableRow key={idx} className="border-[#2B4B8C]/5">
                        <TableCell className="font-medium text-[#2B4B8C]">{r.worker_name}</TableCell>
                        <TableCell className="text-[#2B4B8C]/60 text-sm">{r.worker_rut || '-'}</TableCell>
                        <TableCell className="text-center text-[#2B4B8C]/70">{r.attempt_number}</TableCell>
                        <TableCell className="text-center font-medium text-[#2B4B8C]">{r.score != null ? `${r.score}%` : '-'}</TableCell>
                        <TableCell className="text-center">
                          {r.result === 'passed' ? (
                            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">Aprobado</Badge>
                          ) : r.result === 'failed' ? (
                            <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">Reprobado</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">Pendiente</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-[#2B4B8C]/60 text-sm">{r.completed_at ? new Date(r.completed_at).toLocaleDateString('es-CL') : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-16 text-[#2B4B8C]/40">
                  <Exam size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay resultados de tests</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Close Tab */}
        <TabsContent value="close">
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-6">
              {isClosed ? (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto mb-3 text-green-500" />
                  <p className="text-lg font-medium text-[#2B4B8C]">Capacitacion Cerrada</p>
                  <p className="text-sm text-[#2B4B8C]/60 mt-1">Esta capacitacion ya fue finalizada</p>
                </div>
              ) : (
                <div className="max-w-md mx-auto text-center py-8">
                  <Warning size={48} className="mx-auto mb-3 text-amber-500" />
                  <p className="text-lg font-medium text-[#2B4B8C]">Cerrar Capacitacion</p>
                  <p className="text-sm text-[#2B4B8C]/60 mt-2">
                    Al cerrar la capacitacion, su estado cambiara a "Realizada". Asegurate de haber completado la asistencia y evaluacion antes de cerrar.
                  </p>
                  <div className="mt-4 space-y-2 text-left bg-[#f8fafc] rounded-lg p-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#2B4B8C]/60">Inscritos:</span>
                      <span className="font-medium text-[#2B4B8C]">{training.enrollments.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#2B4B8C]/60">Con asistencia:</span>
                      <span className="font-medium text-[#2B4B8C]">{training.enrollments.filter(e => e.attended).length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#2B4B8C]/60">Tests completados:</span>
                      <span className="font-medium text-[#2B4B8C]">{training.test_results?.length || 0}</span>
                    </div>
                  </div>
                  <Button onClick={() => setCloseConfirm(true)} className="mt-6 bg-red-500 text-white hover:bg-red-600" data-testid="close-training-btn">
                    Cerrar Capacitacion
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Close confirmation */}
      <Dialog open={closeConfirm} onOpenChange={setCloseConfirm}>
        <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
              <Warning size={20} className="text-red-500" /> Confirmar cierre
            </DialogTitle>
            <DialogDescription className="text-[#2B4B8C]/60 pt-1">
              Esta accion cambiara el estado de la capacitacion a "Realizada". No se podra revertir.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setCloseConfirm(false)} className="flex-1 border-[#2B4B8C]/10">Cancelar</Button>
            <Button onClick={handleClose} disabled={closing} className="flex-1 bg-red-500 text-white hover:bg-red-600" data-testid="confirm-close-btn">
              {closing ? 'Cerrando...' : 'Confirmar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
